import './styles/style.scss';

const TOOLTIP_VISIBLE_CLASS = 'ra-tooltip--visible';

/**
 * Tooltip component.
 * @param {HTMLElement} options.target - target element to attach tooltip to.
 * @param {string} options.content - content of the tooltip.
 * @param {string} options.id - optional id of the tooltip.
 *
 * A11Y NOTE:
 * If accessible text is same as tooltip text - can use aria-label on target element.
 * Otherwise, if tooltip content provides additional information - can pass an id to the tooltip and use aria-describedby with same id on the target.
 */
export class Tooltip {
  tooltip = null;
  isHovered = false;
  isFocused = false;
  unmountTimeout = null;

  constructor({ target, content, id }) {
    this.target = target;
    this.content = content;
    this.id = id;

    this.boundUpdatePosition = this.updatePosition.bind(this);
    this.boundHandleMouseEnter = this.handleMouseEnter.bind(this);
    this.boundHandleMouseLeave = this.handleMouseLeave.bind(this);
    this.boundHandleFocus = this.handleFocus.bind(this);
    this.boundHandleBlur = this.handleBlur.bind(this);
  }

  mount() {
    if (!this.target || !this.content) {
      console.warn('RefappTooltip: target or content is not defined');
      return;
    }

    this.target.addEventListener('mouseenter', this.boundHandleMouseEnter);
    this.target.addEventListener('mouseleave', this.boundHandleMouseLeave);
    this.target.addEventListener('focus', this.boundHandleFocus);
    this.target.addEventListener('blur', this.boundHandleBlur);
  }

  unmount() {
    if (!this.target) {
      return;
    }

    this.tooltip?.remove();
    window.removeEventListener('resize', this.boundUpdatePosition);

    this.target.removeEventListener('mouseenter', this.boundHandleMouseEnter);
    this.target.removeEventListener('mouseleave', this.boundHandleMouseLeave);
    this.target.removeEventListener('focus', this.boundHandleFocus);
    this.target.removeEventListener('blur', this.boundHandleBlur);
  }

  handleFocus() {
    this.isFocused = true;
    this.showTooltip();
  }

  handleBlur() {
    this.isFocused = false;
    this.hideTooltip();
  }

  handleMouseEnter() {
    this.isHovered = true;
    this.showTooltip();
  }

  handleMouseLeave() {
    this.isHovered = false;
    this.hideTooltip();
  }

  updatePosition() {
    const rect = this.target.getBoundingClientRect();
    const tRect = this.tooltip.getBoundingClientRect();

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    const top = rect.top + scrollTop - tRect.height;
    const left = rect.left + scrollLeft + rect.width / 2 - tRect.width / 2;

    this.tooltip.style.setProperty('--tooltip-top', `${top}px`);
    this.tooltip.style.setProperty('--tooltip-left', `${left}px`);
  }

  showTooltip() {
    if (this.unmountTimeout) {
      this.tooltip.classList.add(TOOLTIP_VISIBLE_CLASS);
      clearTimeout(this.unmountTimeout);
      this.unmountTimeout = null;
      return;
    }

    if (this.tooltip) return;

    this.tooltip = document.createElement('div');
    this.tooltip.role = 'tooltip';
    if (this.id) {
      this.tooltip.id = this.id;
    }
    this.tooltip.classList.add('ra-tooltip', TOOLTIP_VISIBLE_CLASS);
    this.tooltip.innerHTML = this.content;
    document.body.appendChild(this.tooltip);

    this.updatePosition();

    window.addEventListener('resize', this.boundUpdatePosition);
  }

  hideTooltip() {
    if (this.isHovered || this.isFocused || !this.tooltip) return;

    this.tooltip.classList.remove(TOOLTIP_VISIBLE_CLASS);

    const animationDuration = parseInt(
      getComputedStyle(this.tooltip).getPropertyValue('--refapp-tooltip-transition-duration-ms'),
      10,
    );

    this.unmountTimeout = setTimeout(() => {
      this.tooltip.remove();
      this.tooltip = null;
      window.removeEventListener('resize', this.boundUpdatePosition);
      this.unmountTimeout = null;
    }, animationDuration || 0);
  }
}
